import {
  ArrowRight,
  Eye,
  Activity,
  Brain,
  Sparkles,
  Cog,
  Warehouse,
  Truck,
  LayoutGrid,
  ScanSearch,
  Smartphone,
  LayoutDashboard,
  Grid3x3,
  ShieldCheck,
  Target,
  Cpu,
  Cloud,
  Infinity as InfinityIcon,
  Workflow,
  Bot,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import SEO, { ORGANIZATION_SCHEMA, WEBSITE_SCHEMA } from "../components/SEO";

const HERO_PRIVA_IMG =
  "/images/image-1.webp";
const ARCH_IMG =
  "/images/primes4ai-ecosystem-architecture.webp";

// --- Brand tokens ----------------------------------------------------------
// Re-used across the homepage. Keeping them inline avoids touching the global
// CSS for a single page.
const BRAND_BLUE = "#025979";
const BRAND_BLUE_DARK = "#03738C";
const AI_PURPLE = "#7C3AED";

// --- Reusable atoms --------------------------------------------------------
const Eyebrow = ({ children, tone = "blue" }) => (
  <p
    className={`text-[11px] font-semibold tracking-[0.22em] uppercase mb-3 ${
      tone === "purple" ? "text-[#7C3AED]" : "text-[#025979]"
    }`}
  >
    {children}
  </p>
);

const H2 = ({ children, className = "" }) => (
  <h2
    className={`text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight text-[#0F172A] leading-[1.1] ${className}`}
    style={{ fontFamily: "Chivo, sans-serif" }}
  >
    {children}
  </h2>
);

const Lead = ({ children, className = "" }) => (
  <p className={`mt-5 text-base sm:text-lg text-[#475569] leading-relaxed max-w-3xl ${className}`}>
    {children}
  </p>
);

const PrimaryButton = ({ href, onClick, children, testId, full = false }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={testId}
    className={`group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide bg-[${BRAND_BLUE}] hover:bg-[${BRAND_BLUE_DARK}] text-white transition-colors ${full ? "w-full" : ""}`}
    style={{ backgroundColor: BRAND_BLUE }}
    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND_BLUE_DARK)}
    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND_BLUE)}
  >
    {children}
    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
  </a>
);

const OutlineButton = ({ href, onClick, children, testId, tone = "blue" }) => {
  const isPurple = tone === "purple";
  const color = isPurple ? AI_PURPLE : BRAND_BLUE;
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testId}
      className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border bg-white transition-colors"
      style={{ borderColor: color, color }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isPurple ? "#F5F3FF" : "#E0F2FE")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
    >
      {children}
      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
    </a>
  );
};

const Card = ({ children, className = "", testId, accent = "blue", hover = true }) => {
  const ring = accent === "purple" ? "hover:border-[#C4B5FD]" : "hover:border-[#5EB8D8]";
  return (
    <div
      data-testid={testId}
      className={`relative bg-white border border-[#E5E7EB] rounded-sm shadow-sm transition-all duration-200 ${hover ? `${ring} hover:shadow-md hover:-translate-y-0.5` : ""} ${className}`}
    >
      {children}
    </div>
  );
};

const ComingSoonHint = () => (
  <span className="ml-2 inline-flex items-center text-[11px] italic text-[#9CA3AF] tracking-normal">
    Coming soon
  </span>
);

const SectionDivider = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="h-px bg-gradient-to-r from-transparent via-[#E5E7EB] to-transparent" />
  </div>
);

// --- SECTION 1: HERO -------------------------------------------------------
// The PRIVA artwork is the right-side hero visual. The image itself ships
// with a deep-navy background that blends naturally into the section
// gradient, so we let it bleed to the right edge of the viewport on large
// screens for a more dominant, premium feel. All headline / sub / CTA /
// trust text is live HTML on the left so nothing is duplicated inside the
// image.
const Hero = () => (
  <section
    data-testid="primes4-hero"
    className="relative overflow-hidden text-white"
    style={{
      background:
        "linear-gradient(135deg, #0E1A3F 0%, #112452 35%, #0E1A3F 70%, #0B1734 100%)",
    }}
  >
    {/* Soft enterprise grid overlay (masked to center so edges stay calm) */}
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.08]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
      }}
    />
    {/* Ambient blue + purple glows */}
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute -top-40 -left-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(circle, #1E40AF 0%, transparent 65%)" }}
      />
      <div
        className="absolute top-1/3 right-0 w-[560px] h-[560px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 65%)" }}
      />
      <div
        className="absolute -bottom-32 left-1/3 w-[440px] h-[440px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #5EB8D8 0%, transparent 65%)" }}
      />
    </div>
    {/* SAP-style hairline divider at the bottom */}
    <div
      className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(94,184,216,0.5) 50%, transparent 100%)",
      }}
    />

    {/* PRIVA artwork — covers the full hero as a background image. Text and
        CTAs render on top via the relative content layer below. A left-side
        gradient overlay keeps the copy area legible without darkening the
        artwork. */}
    <div
      aria-hidden="true"
      data-testid="hero-priva-visual-bg"
      className="absolute inset-0 pointer-events-none"
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${HERO_PRIVA_IMG})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right center",
          backgroundSize: "cover",
        }}
      />
      {/* Left-side legibility gradient — strongest at the left edge, fully
          transparent past the midline so the artwork stays untouched. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(11,23,52,0.92) 0%, rgba(14,26,63,0.78) 22%, rgba(14,26,63,0.35) 48%, rgba(14,26,63,0) 70%)",
        }}
      />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        {/* Copy */}
        <div className="lg:col-span-7 xl:col-span-7 relative" data-testid="hero-copy">
          <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#5EB8D8] mb-6 flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#5EB8D8]" />
            PrimeS4 AI Ecosystem
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-[56px] xl:text-[60px] font-bold tracking-tight leading-[1.05] max-w-[640px]"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="hero-headline"
          >
            <span>Operational Intelligence </span>
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(90deg, #5EB8D8 0%, #A78BFA 100%)" }}
            >
              for the Enterprise
            </span>
          </h1>
          <p
            className="mt-7 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl"
            data-testid="hero-sub"
          >
            PRIVA-powered, SAP-aligned operational intelligence for enterprise transformation and AI-assisted execution.
          </p>

          <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-ctas">
            {/* Hero "Explore PRIVA" CTA — bright enterprise blue, distinct
                from the section CTAs elsewhere on the page. */}
            <a
              href="/priva"
              data-testid="hero-cta-priva"
              className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide text-white transition-colors"
              style={{ backgroundColor: "#2563EB" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1D4ED8")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2563EB")}
            >
              Explore PRIVA
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
            </a>
            <a
              href="/platforms"
              data-testid="hero-cta-platforms"
              className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors"
            >
              Explore Platforms
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
            </a>
          </div>

          {/* Trust indicators */}
          <div
            className="mt-14 grid sm:grid-cols-3 gap-5 max-w-2xl"
            data-testid="hero-trust"
          >
            {[
              { icon: ShieldCheck, t1: "SAP-Aligned.", t2: "Future-Ready." },
              { icon: Brain, t1: "AI-Powered.", t2: "Human-Centered." },
              { icon: Target, t1: "Enterprise-Grade.", t2: "Outcome-Driven." },
            ].map((it) => (
              <div
                key={it.t1}
                className="flex items-start gap-3 text-[11px] font-semibold tracking-[0.14em] uppercase text-white/85"
              >
                <it.icon className="w-4 h-4 mt-0.5 shrink-0 text-[#5EB8D8]" strokeWidth={1.5} />
                <div>
                  <div>{it.t1}</div>
                  <div className="text-white/65">{it.t2}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Spacer column reserved for the artwork area on lg+. The artwork
            itself is the full-width background layer above. */}
        <div className="hidden lg:block lg:col-span-5 xl:col-span-5" aria-hidden="true" />
      </div>
    </div>
  </section>
);

// --- SECTION 2: PRIVA ------------------------------------------------------
const Priva = () => {
  const capabilities = [
    { icon: Eye, title: "Unified Visibility", desc: "Cross-platform operational truth in one pane." },
    { icon: Activity, title: "Intelligent Execution", desc: "Context-aware actions across every workflow." },
    { icon: Brain, title: "Operational Intelligence", desc: "Live signals, anomalies, and exceptions." },
    { icon: Sparkles, title: "AI-Assisted Decisions", desc: "Contextual recommendations grounded in operations." },
    { icon: Cog, title: "Enterprise Automation", desc: "Process-level automation across the ecosystem." },
  ];
  return (
    <section
      id="priva"
      data-testid="section-priva"
      className="relative py-20 lg:py-28 bg-gradient-to-b from-white via-[#FAFAFF] to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow tone="purple">PRIVA</Eyebrow>
          <H2>PRIVA — Operational Intelligence Layer</H2>
          <Lead>
            PRIVA orchestrates enterprise operations, intelligent execution, operational visibility, automation, and contextual decision support across platforms and applications.
          </Lead>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="priva-capabilities">
          {capabilities.map((c) => (
            <Card key={c.title} accent="purple" className="p-5" testId={`priva-cap-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div
                className="w-9 h-9 rounded-sm flex items-center justify-center mb-4"
                style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)" }}
              >
                <c.icon className="w-4.5 h-4.5 text-[#7C3AED]" strokeWidth={1.6} />
              </div>
              <h3 className="text-[15px] font-semibold text-[#0F172A] leading-snug">{c.title}</h3>
              <p className="mt-2 text-[13px] text-[#475569] leading-relaxed">{c.desc}</p>
            </Card>
          ))}
        </div>

        <div className="mt-10">
          <OutlineButton href="/priva" testId="priva-cta" tone="purple">
            Explore PRIVA
          </OutlineButton>
        </div>
      </div>
    </section>
  );
};

// --- SECTION 3: PLATFORMS --------------------------------------------------
const Platforms = () => (
  <section
    id="platforms"
    data-testid="section-platforms"
    className="relative py-20 lg:py-28"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <Eyebrow>Platforms</Eyebrow>
        <H2>Industry Platforms for Intelligent Operations</H2>
        <Lead>
          Purpose-built platforms designed to accelerate operational transformation across warehousing, logistics, transportation, and connected enterprise operations.
        </Lead>
      </div>

      <div className="mt-12 grid lg:grid-cols-12 gap-5" data-testid="platforms-grid">
        {/* WarehouseX — dominant */}
        <Card className="lg:col-span-7 p-7 sm:p-9 overflow-hidden group" testId="platform-warehousex">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div
                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm text-[10px] font-semibold tracking-[0.18em] uppercase mb-5"
                style={{ background: "#E0F2FE", color: BRAND_BLUE }}
              >
                <Warehouse className="w-3.5 h-3.5" strokeWidth={1.8} />
                WarehouseX
              </div>
              <h3
                className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A] leading-tight"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                AI-native warehouse transformation and operational intelligence platform.
              </h3>
              <p className="mt-4 text-[15px] text-[#475569] leading-relaxed max-w-xl">
                A unified platform for evaluating, transforming, executing, and operating modern SAP warehouses — powered by PRIVA intelligence.
              </p>
              <div className="mt-7">
                <PrimaryButton href="/warehousex" testId="platform-warehousex-cta">
                  Explore WarehouseX
                </PrimaryButton>
              </div>
            </div>
            <Warehouse
              className="hidden sm:block w-20 h-20 lg:w-28 lg:h-28 text-[#5EB8D8] opacity-25 shrink-0"
              strokeWidth={1.2}
            />
          </div>
          <div
            className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full opacity-30 blur-3xl pointer-events-none"
            style={{ background: "radial-gradient(circle, #BAE6FD 0%, transparent 65%)" }}
          />
        </Card>

        {/* TransportX */}
        <Card className="lg:col-span-5 p-6 sm:p-7" testId="platform-transportx">
          <div className="flex items-center gap-2 mb-4">
            <Truck className="w-4 h-4 text-[#6B7280]" strokeWidth={1.6} />
            <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#6B7280]">
              TransportX
            </span>
            <ComingSoonHint />
          </div>
          <h3
            className="text-xl font-semibold tracking-tight text-[#0F172A] leading-snug"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Intelligent transportation and logistics operations platform.
          </h3>
          <p className="mt-3 text-[13px] text-[#475569] leading-relaxed">
            Extending the PrimeS4 AI ecosystem into intelligent transport orchestration and connected logistics execution.
          </p>
        </Card>

        {/* Future Platforms */}
        <Card className="lg:col-span-12 p-6 sm:p-7" testId="platform-future">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0"
                style={{ background: "#F1F5F9" }}
              >
                <LayoutGrid className="w-4.5 h-4.5 text-[#6B7280]" strokeWidth={1.6} />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-[#0F172A]">Future Platforms</h3>
                <p className="text-[13px] text-[#475569] mt-0.5">
                  Expanding into new operational domains across the connected enterprise.
                </p>
              </div>
            </div>
            <span className="text-[11px] italic text-[#9CA3AF]">Continuously evolving</span>
          </div>
        </Card>
      </div>

      <div className="mt-10">
        <OutlineButton href="/platforms" testId="platforms-cta">
          Explore Platforms
        </OutlineButton>
      </div>
    </div>
  </section>
);

// --- SECTION 4: ENTERPRISE APPLICATIONS ------------------------------------
const EnterpriseApps = () => {
  const apps = [
    {
      icon: ScanSearch,
      label: "SmartInspect",
      desc: "AI-powered inspection and compliance platform.",
      href: "/warehousex/smartintegrations/smartinspect",
    },
    {
      icon: Smartphone,
      label: "SmartRF",
      desc: "Intelligent warehouse mobility and workforce execution solution.",
      href: "/warehousex/smartexecution/smartrf",
    },
    {
      icon: LayoutDashboard,
      label: "Command Center",
      desc: "Operational monitoring and execution visibility platform.",
      comingSoon: true,
    },
    {
      icon: Grid3x3,
      label: "Future Applications",
      desc: "Continuously expanding enterprise application portfolio.",
      muted: true,
    },
  ];
  return (
    <section
      id="enterprise-apps"
      data-testid="section-enterprise-apps"
      className="relative py-20 lg:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow>Enterprise Applications</Eyebrow>
          <H2>Enterprise Applications Built for Execution</H2>
          <Lead>
            Operational applications designed to enhance visibility, execution, automation, mobility, and operational excellence.
          </Lead>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="apps-grid">
          {apps.map((a) => {
            const isLink = !a.comingSoon && !a.muted;
            const Wrap = isLink ? "a" : "div";
            const wrapProps = isLink ? { href: a.href } : {};
            return (
              <Wrap
                key={a.label}
                {...wrapProps}
                data-testid={`app-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`block bg-white border border-[#E5E7EB] rounded-sm p-6 transition-all duration-200 ${
                  isLink ? "hover:border-[#5EB8D8] hover:shadow-md hover:-translate-y-0.5" : ""
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-sm flex items-center justify-center mb-5 ${
                    a.muted || a.comingSoon ? "bg-[#F1F5F9]" : ""
                  }`}
                  style={
                    a.muted || a.comingSoon
                      ? {}
                      : { background: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)" }
                  }
                >
                  <a.icon
                    className={`w-4.5 h-4.5 ${a.muted || a.comingSoon ? "text-[#9CA3AF]" : "text-[#025979]"}`}
                    strokeWidth={1.6}
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-[15px] font-semibold text-[#0F172A]">{a.label}</h3>
                  {a.comingSoon && <ComingSoonHint />}
                </div>
                <p className="mt-2 text-[13px] text-[#475569] leading-relaxed">{a.desc}</p>
                {isLink && (
                  <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold tracking-wide text-[#025979]">
                    Open
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                  </div>
                )}
              </Wrap>
            );
          })}
        </div>

        <div className="mt-10">
          <OutlineButton href="/enterprise-apps" testId="enterprise-apps-cta">
            Explore Enterprise Applications
          </OutlineButton>
        </div>
      </div>
    </section>
  );
};

// --- SECTION 5: BUILT FOR THE AUTONOMOUS ENTERPRISE ------------------------
const Autonomous = () => (
  <section
    id="autonomous"
    data-testid="section-autonomous"
    className="py-20 lg:py-28 bg-white"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto text-center">
        <Eyebrow>Architecture</Eyebrow>
        <H2>Built for the Autonomous Enterprise</H2>
        <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed">
          PrimeS4 AI combines operational intelligence, enterprise applications, intelligent platforms, and SAP-native AI foundations to support connected enterprise operations across supply chain, logistics, warehousing, and beyond.
        </p>
      </div>
      <div className="mt-12 lg:mt-16 max-w-6xl mx-auto">
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
          <img
            src={ARCH_IMG}
            alt="PrimeS4 AI Ecosystem Architecture — PRIVA orchestrating Platforms, Enterprise Applications, Operational Intelligence, and SAP-Native Foundation"
            className="w-full h-auto block"
            loading="lazy"
            data-testid="autonomous-architecture-image"
          />
        </div>
      </div>
    </div>
  </section>
);

// --- SECTION 6: INNOVATION -------------------------------------------------
const Innovation = () => {
  // SAP-related items intentionally first.
  const items = [
    { label: "SAP BTP", icon: Cloud, sap: true },
    { label: "SAP Business AI", icon: Sparkles, sap: true },
    { label: "Joule", icon: InfinityIcon, sap: true },
    { label: "OpenAI", icon: Brain, sap: false },
    { label: "Claude", icon: Brain, sap: false },
    { label: "Gemini", icon: Sparkles, sap: false },
    { label: "Enterprise Integration", icon: Workflow, sap: false },
    { label: "AI Orchestration", icon: Bot, sap: false },
  ];
  return (
    <section
      id="innovation"
      data-testid="section-innovation"
      className="relative py-20 lg:py-28 bg-gradient-to-b from-[#F8FAFC] to-white border-t border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow>Innovation</Eyebrow>
          <H2>SAP-Native AI Innovation &amp; Enterprise Orchestration</H2>
          <Lead>
            Built around SAP BTP, SAP Business AI, Joule, and modern AI frameworks to support scalable, secure, and intelligent enterprise operations.
          </Lead>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="innovation-grid">
          {items.map((it) => (
            <div
              key={it.label}
              data-testid={`innovation-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`group flex items-center gap-3 px-5 py-4 rounded-sm border bg-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                it.sap
                  ? "border-[#BAE6FD] hover:border-[#0093cb]"
                  : "border-[#E5E7EB] hover:border-[#C4B5FD]"
              }`}
            >
              <div
                className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                style={{
                  background: it.sap
                    ? "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)"
                    : "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
                }}
              >
                <it.icon
                  className={`w-4 h-4 ${it.sap ? "text-[#025979]" : "text-[#7C3AED]"}`}
                  strokeWidth={1.6}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-[#0F172A] truncate">{it.label}</div>
                {it.sap && (
                  <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#025979] mt-0.5">
                    SAP
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <OutlineButton href="/innovation" testId="innovation-cta">
            Explore Innovation
          </OutlineButton>
        </div>
      </div>
    </section>
  );
};

// --- SECTION 7: WHY PRIMES4 AI --------------------------------------------
const WhyPrimes4 = () => {
  const statements = [
    { t1: "Purpose-built AI", t2: "for enterprise operations.", icon: Target },
    { t1: "Delivered with", t2: "SAP DNA.", icon: ShieldCheck },
    { t1: "Designed for", t2: "real-world impact.", icon: Sparkles },
  ];
  return (
    <section
      id="why"
      data-testid="section-why"
      className="relative py-24 lg:py-32 bg-[#070B1F] text-white overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/3 w-[420px] h-[420px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #025979 0%, transparent 60%)" }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[360px] h-[360px] rounded-full opacity-15 blur-3xl"
          style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 60%)" }}
        />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl">
          <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#5EB8D8] mb-5 flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#5EB8D8]" />
            Why PrimeS4 AI
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.1]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Operational intelligence, enterprise applications, SAP-native innovation, and intelligent execution — engineered for the connected enterprise.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed max-w-3xl">
            PrimeS4 AI combines operational intelligence, enterprise applications, SAP-native innovation, and intelligent execution frameworks to help organizations modernize connected enterprise operations.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-3 gap-6" data-testid="why-statements">
          {statements.map((s) => (
            <div
              key={s.t1}
              className="relative pl-5 border-l border-white/15"
              data-testid={`why-${s.t1.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <s.icon className="w-5 h-5 text-[#5EB8D8] mb-4" strokeWidth={1.6} />
              <p
                className="text-xl sm:text-2xl font-semibold tracking-tight leading-snug"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                {s.t1} <span className="text-white/75 font-normal">{s.t2}</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- SECTION 8: FINAL CTA --------------------------------------------------
const FinalCta = () => (
  <section
    data-testid="section-final-cta"
    className="relative py-20 lg:py-24 bg-white"
  >
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <Eyebrow>Get started</Eyebrow>
      <h2
        className="text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight text-[#0F172A] leading-[1.1]"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Build the Intelligent Enterprise with PrimeS4 AI
      </h2>
      <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed max-w-3xl mx-auto">
        Operational intelligence, enterprise AI, and intelligent execution platforms designed for the future of connected operations.
      </p>
      <div className="mt-9 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          data-testid="final-cta-request-demo"
          onClick={() => window.dispatchEvent(new CustomEvent("requestDemo:open"))}
          className="inline-flex items-center justify-center gap-2 rounded-sm h-11 px-7 text-xs font-semibold tracking-wide bg-[#025979] hover:bg-[#03738C] text-white transition-colors"
        >
          Request Demo
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
        <OutlineButton href="/warehousex" testId="final-cta-warehousex">
          Explore WarehouseX
        </OutlineButton>
      </div>
    </div>
  </section>
);

const PrimeS4Home = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="primes4-home">
    <SEO
      title="PrimeS4 AI | SAP, SCM & AI Transformation"
      description="PrimeS4 AI delivers operational intelligence, SAP-aligned AI platforms, warehouse transformation, and enterprise execution innovation powered by PRIVA and WarehouseX."
      keywords={[
        "PrimeS4 AI",
        "SAP AI",
        "SAP S/4HANA transformation",
        "Operational Intelligence",
        "Enterprise AI",
        "SAP SCM transformation",
        "Intelligent Enterprise",
        "PRIVA",
        "WarehouseX",
      ]}
      canonical="/"
      imageAlt="PrimeS4 AI — Enterprise AI orchestration ecosystem"
      schema={[ORGANIZATION_SCHEMA, WEBSITE_SCHEMA]}
    />
    <Header />
    <main className="flex-1">
      <Hero />
      <Priva />
      <SectionDivider />
      <Platforms />
      <EnterpriseApps />
      <Autonomous />
      <Innovation />
      <WhyPrimes4 />
      <FinalCta />
    </main>
    <Footer />
  </div>
);

export default PrimeS4Home;
